import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { moduleHome } from "@rentorng/core";
import { useActiveModule } from "@/lib/module-rail";
import { useSession } from "./lib/session";
import { Spinner } from "./components/ui";
import { AppLayout } from "./components/layout";
import { Require } from "./components/require";
import { ModuleDoor } from "./pages/ModuleLocked";
import { LandingPage } from "./pages/Landing";
import { SignInPage } from "./pages/SignIn";
import { SignUpPage } from "./pages/SignUp";
import { ForgotPasswordPage } from "./pages/ForgotPassword";
import { ResetPasswordPage } from "./pages/ResetPassword";
import { VerifyEmailPage } from "./pages/VerifyEmail";
import { PortalEnterPage, PortalPage, PortalSignInPage } from "./pages/Portal";
import { PortalSetPasswordPage, PortalSignUpPage } from "./pages/PortalAuth";
import { PortalPurchasePage, PortalPurchaseStatementPage } from "./pages/PortalPurchase";
import { PortalAgreementPage } from "./pages/PortalAgreement";
import { PortalInvoicePage } from "./pages/PortalInvoice";
import { PortalCommissionPage, PortalCommissionStatementPage } from "./pages/PortalCommission";
import { SecurityPage } from "./pages/Security";
import { DashboardPage } from "./pages/Dashboard";
import { GettingStartedPage } from "./pages/GettingStarted";
import { PropertiesPage } from "./pages/Properties";
import { DevelopmentPropertiesPage, DevelopmentPropertyForm } from "./pages/DevelopmentProperties";
import { DeveloperOverview } from "./pages/DevelopmentOverview";
import { SalesPage } from "./pages/DevelopmentSales";
import { BuyerDetailPage, BuyersPage } from "./pages/DevelopmentBuyers";
import { DevelopmentPropertyDetailPage } from "./pages/DevelopmentPropertyDetail";
import { PurchaseForm } from "./pages/Purchases";
import { PurchaseDetailPage } from "./pages/PurchaseDetail";
import { PurchaseStatementPage } from "./pages/PurchaseStatement";
import { OfferLetterPage } from "./pages/OfferLetter";
import { ReservationDetailPage } from "./pages/Reservations";
import { MarketerDetailPage, MarketerStatementPage, MarketersPage } from "./pages/Marketers";
import { PropertyFormPage } from "./pages/PropertyForm";
import { PropertyDetailPage } from "./pages/PropertyDetail";
import { UnitsPage } from "./pages/Units";
import { UnitFormPage } from "./pages/UnitForm";
import { ClientsPage } from "./pages/Clients";
import { ClientFormPage } from "./pages/ClientForm";
import { ClientDetailPage } from "./pages/ClientDetail";
import { LeasesPage } from "./pages/Leases";
import { LeaseNewPage } from "./pages/LeaseNew";
import { LeaseDetailPage } from "./pages/LeaseDetail";
import { AgreementDetailPage, AgreementsPage } from "./pages/Agreements";
import { InvoicesPage } from "./pages/Invoices";
import { InvoiceDetailPage } from "./pages/InvoiceDetail";
import { InvoiceNewPage } from "./pages/InvoiceNew";
import { PaymentsPage } from "./pages/Payments";
import { PaymentNewPage } from "./pages/PaymentNew";
import { DeclaredPaymentsPage } from "./pages/DeclaredPayments";
import { PaymentDetailPage } from "./pages/PaymentDetail";
import { ExpensesPage } from "./pages/Expenses";
import { MaintenancePage } from "./pages/Maintenance";
import { InspectionsPage } from "./pages/Inspections";
import { InspectionDetailPage } from "./pages/InspectionDetail";
import { InspectionReportPage } from "./pages/InspectionReport";
import { RemindersPage } from "./pages/Reminders";
import { ReminderSendPage } from "./pages/ReminderSend";
import { TemplatesPage } from "./pages/Templates";
import { ReportsPage } from "./pages/Reports";
/* The reports are a hub of pages rather than a strip of tabs, so each one is its own route and
   each is bookmarkable — `apps/web/src/pages/Reports.tsx` says why. */
import { CollectionsReportPage } from "./pages/reports/Collections";
import { ArrearsReportPage } from "./pages/reports/Arrears";
import { MoneyReportPage } from "./pages/reports/MoneyFlow";
import { OccupancyReportPage } from "./pages/reports/Occupancy";
import { PipelineReportPage } from "./pages/reports/Pipeline";
import { RentRollReportPage } from "./pages/reports/RentRoll";
import { ExpiriesReportPage } from "./pages/reports/Expiries";
import { SalesReportPage } from "./pages/reports/SalesBook";
import { StatementPage } from "./pages/Statement";
import { SettingsPage } from "./pages/Settings";
import { TeamPage } from "./pages/Team";
import { BankAccountsPage } from "./pages/BankAccountsPage";
import { BillingPage } from "./pages/Billing";
import { AuditPage } from "./pages/Audit";
import { ApiKeysPage } from "./pages/ApiKeys";
import { SupportPage } from "./pages/Support";
import { DataTransferPage } from "./pages/DataTransfer";
import { SettlementsPage } from "./pages/Settlements";
import { PayoutsPage } from "./pages/Payouts";
import { BeneficiariesPage } from "./pages/Beneficiaries";
import { ListsPage } from "./pages/Lists";
import { PayLinkPage } from "./pages/PayLink";
import { ListingsPage } from "./pages/Listings";
import { EnquiriesPage } from "./pages/Enquiries";
import { NotFoundPage } from "./pages/NotFound";

/**
 * Every route in the product.
 *
 * `/app/*` is the manager application and requires a session — `AppLayout` redirects to sign-in
 * if there isn't one. Everything above it is public: signing in, signing up, the page that says
 * what this is, a pay link a tenant was sent, and an agency's public vacancy list.
 */
/**
 * `/app/leases/:id` — the address a tenancy used to have.
 *
 * A component rather than a `<Navigate>` because the id has to travel: the others are fixed
 * addresses, this one is somebody's bookmark of one particular tenancy, and dropping them on the
 * list would make them find it again.
 */
function OldTenancyLink() {
  const { id } = useParams();
  return <Navigate to={`/app/tenancies/${id}`} replace />;
}

/**
 * An address that has moved, kept working.
 *
 * The development module used to live at `/app/estates` and `/app/purchases`; it now lives under
 * `/app/development`, because the word "property" means one thing on each side of the business and
 * only the prefix can say which is meant — see `moduleForPath` in `packages/core/src/modules.ts`,
 * which reads the module out of the address and cannot have two modules owning one word.
 *
 * Those old addresses are in the wild: bookmarked, pasted into emails to colleagues, sent to
 * buyers. An address that used to work and now says "page not found" is worse than one that never
 * existed, so every one of them lands on its replacement.
 *
 * A component rather than a bare `<Navigate>` for two reasons, and both are somebody's bookmark:
 * the **id has to travel**, so `/app/purchases/:id` reaches that purchase rather than the list; and
 * the **query has to travel** with it, so `/app/purchases?arrears=1` — which is what the overview
 * used to link — still opens the filtered list rather than all of them. `replace`, so the back
 * button leaves the module instead of bouncing between the two addresses.
 */
function Moved({ to }: { to: string }) {
  const params = useParams();
  const { search, hash } = useLocation();
  const path = to.replace(/:(\w+)/g, (_, name: string) => params[name] ?? "");
  return <Navigate to={`${path}${search}${hash}`} replace />;
}

/**
 * What `/app` is, for this organisation.
 *
 * **The dashboard is a letting screen**, and an organisation that only sells plots has no rent
 * roll, no arrears and no occupancy — so sending them there is sending them to four zeroes and a
 * rail they cannot use. They go to their own module's home instead.
 *
 * Here rather than in the sign-in redirect, because `/app` is also what a bookmark, an email link
 * and the logo in the corner all point at.
 */
/**
 * `/app`, resolved against the module the reader is **in** rather than the ones they hold.
 *
 * It used `homeFor(modules)`, which answers "where does this account start" — and for an
 * organisation holding both, that is always letting. So `/app` served the letting dashboard
 * whichever module somebody was in: a developer pressed the mark in the header, landed on arrears
 * and occupancy, and every tile there is a letting link, so the next press moved them to the
 * letting module outright. They reported exactly that.
 *
 * The two modules are meant to be independent, and independence has to hold at the **address that
 * belongs to neither**. `/app` is that address, so it asks which module is in force and sends the
 * reader to their own home when it is not letting's.
 *
 * `useActiveModule` is called before the early return, because a hook cannot be conditional. It
 * reads the path, then `?module=`, then what was last remembered — so `/app?module=letting` is
 * still the letting dashboard and there is no loop.
 */
function Home() {
  const { loading, modules } = useSession();
  const active = useActiveModule(modules);
  if (loading) return <Spinner />;
  return active === "letting" ? <DashboardPage /> : <Navigate to={moduleHome(active)} replace />;
}

export function App() {
  return (
    <Routes>
      {/*
        The front door is the sign-in form.

        This application is opened by people who already have an account — every day, several times
        a day — and putting a page of marketing in front of that made the common case the slow one.
        Somebody who has not signed up yet is one link away, on the form itself, and `/sign-up`
        remains an address in its own right so the listing site can send them straight to it.

        The marketing page still exists at `/product`, because it is the only place that says what
        this is to somebody who has not been told.
      */}
      <Route path="/" element={<SignInPage />} />
      <Route path="/sign-in" element={<SignInPage />} />
      <Route path="/sign-up" element={<SignUpPage />} />
      <Route path="/product" element={<LandingPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      {/*
        The tenant, owner and buyer side. Its own credential and its own shell — see pages/Portal.tsx.

        Every one of these is public in the routing sense: the portal's session is a bearer token in
        `localStorage`, not the agency's cookie, so `AppLayout` knows nothing about it and each screen
        decides for itself what to do when there is no session. `/portal/enter` is the link's landing
        place, `/portal/sign-in` takes a password or asks for a link, and `/portal/sign-up` is for
        somebody the agency has never sent anything to.
      */}
      <Route path="/portal" element={<PortalPage />} />
      <Route path="/portal/sign-in" element={<PortalSignInPage />} />
      <Route path="/portal/sign-up" element={<PortalSignUpPage />} />
      <Route path="/portal/enter" element={<PortalEnterPage />} />
      <Route path="/portal/password" element={<PortalSetPasswordPage />} />
      {/* Where a tenant, a landlord or a guarantor reads their agreement and signs it. Public in the
          routing sense like every other portal address — the screen asks for the portal's own
          session and shows the sign-in when there is none. */}
      <Route path="/portal/agreements/:id" element={<PortalAgreementPage />} />
      <Route path="/portal/purchases/:id" element={<PortalPurchasePage />} />
      <Route path="/portal/purchases/:id/statement" element={<PortalPurchaseStatementPage />} />
      {/* One invoice with its lines, and — for somebody who sells for the agency rather than rents
          from it — their own commission and its statement. Reached from the account screen, which
          offers the commission link only when `/portal/me` says this person sells here. */}
      <Route path="/portal/invoices/:id" element={<PortalInvoicePage />} />
      <Route path="/portal/commission" element={<PortalCommissionPage />} />
      <Route path="/portal/commission/statement" element={<PortalCommissionStatementPage />} />
      <Route path="/pay/:token" element={<PayLinkPage />} />
      <Route path="/listings/:slug" element={<ListingsPage />} />

      <Route path="/app" element={<AppLayout />}>
        {/*
          The module door.

          A pathless route around everything under `/app`, so an address belonging to a module
          this agency does not hold is answered *in place* — with the rail, the bar and the
          switcher still drawn around it — rather than by a redirect to the other module's
          dashboard. A redirect says the address was a mistake, which is the one explanation that
          is not true, and it is the one somebody acts on by typing it again.

          It reads which module owns the address from `moduleForPath`, the same function the rail
          uses, so it covers **both directions** with one guard and cannot disagree with the
          navigation about which half of the product an address belongs to. Everything neither
          module owns — the dashboard, payments, settings, the team — falls through untouched.

          The API still refuses: `requireModule` on the path prefix in `apps/api/src/index.ts` is
          what actually decides, and this is the screen rather than the rule.
        */}
        <Route element={<ModuleDoor />}>
          <Route index element={<Home />} />

          {/*
            The welcome and the walkthrough.

            A screen rather than a banner, because it is the one thing in the product somebody has to
            be able to *come back to*: a panel that can only be met once, on the dashboard, is a panel
            that is gone the first time somebody dismisses it by accident. It is reachable by anybody
            with a session — the steps inside it are narrowed to what the member may actually do, so
            there is nothing here to guard at the route.
          */}
          <Route path="getting-started" element={<GettingStartedPage />} />

          <Route path="properties" element={<PropertiesPage />} />
          <Route path="properties/new" element={<Require need="property.edit" to="/app/properties"><PropertyFormPage /></Require>} />
          <Route path="properties/:id" element={<PropertyDetailPage />} />
          <Route path="properties/:id/edit" element={<Require need="property.edit" to="/app/properties"><PropertyFormPage /></Require>} />
          <Route path="properties/:propertyId/units/new" element={<Require need="property.edit" to="/app/properties"><UnitFormPage /></Require>} />

          {/*
            The developer module.

            Guarded by permission here as every other route is; the *module* is enforced by the API,
            which answers 402 to an organisation that does not have it. Deliberately not guarded in
            the router as well: a module the plan closed mid-session would otherwise bounce somebody
            to the dashboard with no explanation, where the API's refusal names what happened.
          */}
          {/*
            The whole module sits under `/app/development`, and it has to.

            A developer calls the scheme they are selling a property, exactly as a letting agency
            calls a building one — so the same word now names a different record on each side of the
            business. `moduleForPath` decides which rail to draw from the address alone, which means
            two modules cannot own one prefix: `/app/properties` was already the letting list, and
            putting the development list there would leave the switcher with no way to tell them
            apart. The prefix is what makes the address itself say which half you are in.

            The module's overview keeps `/app/development` and the list of properties hangs beneath
            it, so the overview is not sitting on the noun for one of the things in it.
          */}
          <Route path="development" element={<Require need="property.view" to="/app"><DeveloperOverview /></Require>} />
          <Route path="development/properties" element={<Require need="property.view" to="/app"><DevelopmentPropertiesPage /></Require>} />
          <Route path="development/properties/new" element={<Require need="property.edit" to="/app/development/properties"><DevelopmentPropertyForm /></Require>} />
          <Route path="development/properties/:id" element={<Require need="property.view" to="/app"><DevelopmentPropertyDetailPage /></Require>} />
          {/*
            Selling, at one address.

            **`/app/development/sales`, and Reservations is no longer a place.** A reservation is a
            sale that has not converted — item 5 of `14-DEVELOPMENT-MODULE-PLAN.md` — so it is a
            *state* inside this list rather than a screen beside it. The record still has a page of
            its own, because a hold has terms and a fee and a document; what it does not have any
            more is a rail item, a list and a set of filters duplicating the ones next door.

            A reservation's page hangs under `sales/reservations/:id` rather than keeping its old
            address, and that is not tidiness: the rail lights the item whose path is the longest
            prefix of where you are, so a reservation living outside `sales` would leave the whole
            rail dark on the one screen the reader reached from Sales.
          */}
          <Route path="development/sales" element={<Require need="lease.view" to="/app"><SalesPage /></Require>} />
          <Route path="development/sales/new" element={<Require need="lease.edit" to="/app/development/sales"><PurchaseForm /></Require>} />
          <Route path="development/sales/reservations/:id" element={<Require need="lease.view" to="/app"><ReservationDetailPage /></Require>} />
          <Route path="development/sales/:id" element={<Require need="lease.view" to="/app"><PurchaseDetailPage /></Require>} />
          <Route path="development/sales/:id/statement" element={<Require need="lease.view" to="/app"><PurchaseStatementPage /></Require>} />
          <Route path="development/sales/:id/offer-letter" element={<Require need="lease.view" to="/app"><OfferLetterPage /></Require>} />

          {/*
            Buyers: the client list seen through what they have bought.

            `party.view`, the same permission the client list asks for, because it is the same
            records read a different way — and a buyer is a client with a purchase, which is a rule
            about who appears rather than about who may look. Item 13.
          */}
          <Route path="development/buyers" element={<Require need="party.view" to="/app"><BuyersPage /></Require>} />
          <Route path="development/buyers/:id" element={<Require need="party.view" to="/app"><BuyerDetailPage /></Require>} />

          {/*
            The people who sell on commission.

            `payout.view`, beside a landlord's, because it is the same question about a different
            person: what has somebody earned, what have they been paid, and what is still owed.
          */}
          <Route path="development/marketers" element={<Require need="payout.view" to="/app"><MarketersPage /></Require>} />
          <Route path="development/marketers/:id" element={<Require need="payout.view" to="/app"><MarketerDetailPage /></Require>} />
          <Route path="development/marketers/:id/statement" element={<Require need="payout.view" to="/app"><MarketerStatementPage /></Require>} />

          {/*
            Where selling used to live, one move ago.

            Purchases and Reservations were two rail items and two lists as recently as this
            release, so every one of these addresses is in somebody's history, somebody's bookmark
            and somebody's email. `Moved` carries the query string with it, which matters for
            `?arrears=1` — the Overview's own links were written against the old address.

            The reservations *list* has no successor of its own: the nearest thing to it is Sales
            narrowed to the reserved stage, so that is where it goes, filter and all.
          */}
          <Route path="development/purchases" element={<Moved to="/app/development/sales" />} />
          <Route path="development/purchases/new" element={<Moved to="/app/development/sales/new" />} />
          <Route path="development/purchases/:id" element={<Moved to="/app/development/sales/:id" />} />
          <Route path="development/purchases/:id/statement" element={<Moved to="/app/development/sales/:id/statement" />} />
          <Route path="development/purchases/:id/offer-letter" element={<Moved to="/app/development/sales/:id/offer-letter" />} />
          <Route path="development/reservations" element={<Navigate to="/app/development/sales?stage=reserved" replace />} />
          <Route path="development/reservations/:id" element={<Moved to="/app/development/sales/reservations/:id" />} />

          {/*
            Where the module used to live. Every one of these is somebody's bookmark — see `Moved`.

            `estates/all` was the list back when `/app/estates` was the overview, and it is kept
            because it has been through two moves now: an address that has already been redirected
            once is exactly the one nobody remembers is still being used.
          */}
          <Route path="estates" element={<Moved to="/app/development/properties" />} />
          <Route path="estates/all" element={<Moved to="/app/development/properties" />} />
          <Route path="estates/new" element={<Moved to="/app/development/properties/new" />} />
          <Route path="estates/:id" element={<Moved to="/app/development/properties/:id" />} />
          <Route path="purchases" element={<Moved to="/app/development/sales" />} />
          <Route path="purchases/new" element={<Moved to="/app/development/sales/new" />} />
          <Route path="purchases/:id" element={<Moved to="/app/development/sales/:id" />} />
          <Route path="purchases/:id/statement" element={<Moved to="/app/development/sales/:id/statement" />} />
          <Route path="purchases/:id/offer-letter" element={<Moved to="/app/development/sales/:id/offer-letter" />} />

          <Route path="units" element={<UnitsPage />} />
          <Route path="units/new" element={<Require need="property.edit" to="/app/units"><UnitFormPage /></Require>} />
          <Route path="units/:id/edit" element={<Require need="property.edit" to="/app/units"><UnitFormPage /></Require>} />

          <Route path="enquiries" element={<EnquiriesPage />} />

          <Route path="clients" element={<ClientsPage />} />
          {/* Who a charge is collected for, and which of them the agency holds a balance for.
              `party.view`, matching the buttons on Settings that lead here: a beneficiary is a
              party the agency handles money for, and the people who may read the client list are
              exactly the people who need to correct a name typed in a hurry on a tenancy form. */}
          <Route path="beneficiaries" element={<Require need="party.view" to="/app"><BeneficiariesPage /></Require>} />
          <Route path="clients/new" element={<Require need="party.edit" to="/app/clients"><ClientFormPage /></Require>} />
          <Route path="clients/:id" element={<ClientDetailPage />} />
          <Route path="clients/:id/edit" element={<Require need="party.edit" to="/app/clients"><ClientFormPage /></Require>} />

          {/*
            Tenancies, which is what they are called everywhere a person can see.

            The word in the interface comes from the agency's own vocabulary — most say "tenancy",
            a mall says "lease" — but the address cannot vary with a setting, so it uses the product's
            own noun. It said `leases` while every label said Tenancies, which is the sort of
            mismatch somebody notices in a shared link and then has to explain.
          */}
          <Route path="tenancies" element={<LeasesPage />} />
          <Route path="tenancies/new" element={<Require need="lease.edit" to="/app/tenancies"><LeaseNewPage /></Require>} />
          <Route path="tenancies/:id" element={<LeaseDetailPage />} />

          {/* Agreements sit beside the tenancies they come from. `lease.view` guards them, so
              anybody who can open a tenancy can read the document raised from it; the writes are
              refused by the API on `document.edit` and the screen hides the controls to match. */}
          <Route path="agreements" element={<Require need="lease.view" to="/app"><AgreementsPage /></Require>} />
          <Route path="agreements/:id" element={<Require need="lease.view" to="/app"><AgreementDetailPage /></Require>} />

          {/*
            The old address, kept.

            Every link an agency has pasted into an email or bookmarked points at `/app/leases`, and
            an address that used to work and now shows "page not found" is worse than one that never
            existed. `replace` so the browser's back button does not bounce between the two.
          */}
          <Route path="leases" element={<Navigate to="/app/tenancies" replace />} />
          <Route path="leases/new" element={<Navigate to="/app/tenancies/new" replace />} />
          <Route path="leases/:id" element={<OldTenancyLink />} />

          <Route path="invoices" element={<InvoicesPage />} />
          <Route path="invoices/new" element={<Require need="invoice.edit" to="/app/invoices"><InvoiceNewPage /></Require>} />
          <Route path="invoices/:id" element={<InvoiceDetailPage />} />

          <Route path="payments" element={<PaymentsPage />} />
          <Route path="payments/new" element={<Require need="payment.record" to="/app/payments"><PaymentNewPage /></Require>} />
          <Route path="payments/declared" element={<DeclaredPaymentsPage />} />
          <Route path="payments/:id" element={<PaymentDetailPage />} />

          <Route path="expenses" element={<Require need="expense.view" to="/app"><ExpensesPage /></Require>} />
          <Route path="maintenance" element={<MaintenancePage />} />
          {/* Inspections. Guarded by the maintenance permissions rather than by ones of their own:
              an inspection is the visit a maintenance job comes out of, and the field agent who
              records one is exactly the role that already holds them. */}
          <Route path="inspections" element={<Require need="maintenance.view" to="/app"><InspectionsPage /></Require>} />
          <Route path="inspections/:id" element={<Require need="maintenance.view" to="/app"><InspectionDetailPage /></Require>} />
          <Route path="inspections/:id/report" element={<Require need="maintenance.view" to="/app"><InspectionReportPage /></Require>} />

          <Route path="reminders" element={<RemindersPage />} />
          <Route path="reminders/send" element={<Require need="reminder.send" to="/app/reminders"><ReminderSendPage /></Require>} />
          <Route path="reminders/templates" element={<Require need="reminder.configure" to="/app/reminders"><TemplatesPage /></Require>} />

          <Route path="reports" element={<Require need="report.view" to="/app"><ReportsPage /></Require>} />
          <Route path="reports/collections" element={<Require need="report.view" to="/app"><CollectionsReportPage /></Require>} />
          <Route path="reports/arrears" element={<Require need="report.view" to="/app"><ArrearsReportPage /></Require>} />
          <Route path="reports/money" element={<Require need="report.view" to="/app"><MoneyReportPage /></Require>} />
          <Route path="reports/occupancy" element={<Require need="report.view" to="/app"><OccupancyReportPage /></Require>} />
          <Route path="reports/pipeline" element={<Require need="report.view" to="/app"><PipelineReportPage /></Require>} />
          <Route path="reports/rent-roll" element={<Require need="report.view" to="/app"><RentRollReportPage /></Require>} />
          <Route path="reports/expiries" element={<Require need="report.view" to="/app"><ExpiriesReportPage /></Require>} />
          <Route path="reports/sales" element={<Require need="report.view" to="/app"><SalesReportPage /></Require>} />
          <Route path="reports/statement/:landlordId" element={<Require need="report.view" to="/app"><StatementPage /></Require>} />

          <Route path="settings" element={<Require need="org.settings" to="/app"><SettingsPage /></Require>} />
          <Route path="settings/team" element={<Require need="org.members" to="/app"><TeamPage /></Require>} />
          <Route path="settings/security" element={<SecurityPage />} />
          <Route path="bank-accounts" element={<Require need="org.settings" to="/app"><BankAccountsPage /></Require>} />
          {/* The old home of the bank details, kept so a bookmark still lands somewhere. */}
          <Route path="settings/bank-accounts" element={<Navigate to="/app/bank-accounts" replace />} />
          <Route path="team" element={<Navigate to="/app/settings/team" replace />} />
          <Route path="settings/billing" element={<Require need="org.billing" to="/app"><BillingPage /></Require>} />
          <Route path="settings/audit" element={<Require need="audit.view" to="/app"><AuditPage /></Require>} />
          <Route path="settings/api-keys" element={<Require need="api.manage" to="/app"><ApiKeysPage /></Require>} />
          {/* Every list the agency keeps, on one screen, because they are one mechanism —
              ADR-0134. Deliberately not in the rail: entries are added from the picker on the
              form that needs them, and this is the tidying afterwards, reached from Settings. */}
          <Route path="settings/lists" element={<Require need="party.view" to="/app"><ListsPage /></Require>} />
          {/*
            Import and export.

            Guarded on `report.view` because that is the permission the other "take the data out"
            screen already uses, and the page offers both halves at once — what a role may actually
            export or import is decided per list by the API, which answers with only the ones that
            caller can have.
          */}
          <Route path="data" element={<Require need="report.view" to="/app"><DataTransferPage /></Require>} />
          <Route path="support" element={<SupportPage />} />
          <Route path="settlements" element={<Require need="payout.view" to="/app"><SettlementsPage /></Require>} />
          <Route path="payouts" element={<Require need="payout.view" to="/app"><PayoutsPage /></Require>} />
        </Route>
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
